<script setup>
// Propriedades de site do cliente (WordPress/Astro): tokens de beacon e de
// conversao, pixel de servidor e a regra de quem manda o evento pra Meta.
// O analogo das landing pages pros sites que nao sao nossos.
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { toast } from 'dashboard/composables/useToast';
import WebPropertiesAPI from 'dashboard/api/webProperties';
import MetaCapiPixelsAPI from 'dashboard/api/metaCapiPixels';
import SettingsPageLayout from 'dashboard/components-next/Settings/SettingsPageLayout.vue';
import SettingsTable from 'dashboard/components-next/Settings/SettingsTable.vue';
import SettingsListToolbar from 'dashboard/components-next/Settings/SettingsListToolbar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { copyTextToClipboard } from 'shared/helpers/clipboard';

const { t } = useI18n();

const SENDERS = ['crm', 'site', 'none'];
const HEADER_NAME = 'X-Smart2-Secret';

const properties = ref([]);
const pixels = ref([]);
const isLoading = ref(true);
const isSaving = ref(false);
const searchQuery = ref('');
const newName = ref('');
const newDomain = ref('');
const revealed = ref({});

const apiError = error =>
  error?.response?.data?.error ||
  (error?.response?.data?.message || []).toString() ||
  t('SITES.GENERIC_ERROR');

const filtered = computed(() => {
  const q = searchQuery.value.trim().toLowerCase();
  if (!q) return properties.value;
  return properties.value.filter(
    p =>
      p.name?.toLowerCase().includes(q) || p.domain?.toLowerCase().includes(q)
  );
});

const headers = computed(() => [
  t('SITES.COLS.SITE'),
  t('SITES.COLS.TOKENS'),
  t('SITES.COLS.META'),
  '',
]);

const fetchAll = async () => {
  isLoading.value = true;
  try {
    const [{ data }, pixelsRes] = await Promise.all([
      WebPropertiesAPI.get(),
      MetaCapiPixelsAPI.get().catch(() => ({ data: [] })),
    ]);
    properties.value = data;
    const list =
      pixelsRes?.data?.payload?.pixels ||
      pixelsRes?.data?.payload ||
      pixelsRes?.data ||
      [];
    pixels.value = (Array.isArray(list) ? list : []).filter(p => p.enabled);
  } catch (error) {
    toast.error(apiError(error));
  } finally {
    isLoading.value = false;
  }
};

const replaceRow = updated => {
  properties.value = properties.value.map(p =>
    p.id === updated.id ? updated : p
  );
};

const createProperty = async () => {
  if (!newName.value.trim() || !newDomain.value.trim()) return;
  isSaving.value = true;
  try {
    const { data } = await WebPropertiesAPI.create({
      name: newName.value.trim(),
      domain: newDomain.value.trim(),
    });
    properties.value = [...properties.value, data];
    newName.value = '';
    newDomain.value = '';
    toast.success(t('SITES.CREATED'));
  } catch (error) {
    toast.error(apiError(error));
  } finally {
    isSaving.value = false;
  }
};

const saveField = async (property, field, value) => {
  try {
    const { data } = await WebPropertiesAPI.update(property.id, {
      [field]: value,
    });
    replaceRow(data);
    toast.success(t('SITES.SAVED'));
  } catch (error) {
    toast.error(apiError(error));
  }
};

const regenerate = async property => {
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('SITES.REGENERATE_CONFIRM'))) return;
  try {
    const { data } = await WebPropertiesAPI.regenerateSecret(property.id);
    replaceRow(data);
    toast.success(t('SITES.REGENERATED'));
  } catch (error) {
    toast.error(apiError(error));
  }
};

const remove = async property => {
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('SITES.DELETE_CONFIRM', { domain: property.domain })))
    return;
  try {
    await WebPropertiesAPI.delete(property.id);
    properties.value = properties.value.filter(p => p.id !== property.id);
    toast.success(t('SITES.DELETED'));
  } catch (error) {
    toast.error(apiError(error));
  }
};

const copy = async value => {
  await copyTextToClipboard(value);
  toast.success(t('SITES.COPIED'));
};

const toggleReveal = property => {
  revealed.value = {
    ...revealed.value,
    [property.id]: !revealed.value[property.id],
  };
};

const masked = value => (value ? `${value.slice(0, 6)}${'•'.repeat(10)}` : '');

onMounted(fetchAll);
</script>

<template>
  <SettingsPageLayout
    :title="t('SITES.HEADER')"
    :description="t('SITES.DESCRIPTION')"
    :kicker="t('SITES.KICKER')"
  >
    <div v-if="isLoading" class="flex justify-center py-16">
      <Spinner :size="28" class="text-n-brand" />
    </div>

    <template v-else>
      <!-- Cadastro: nome + dominio -->
      <div class="rounded-xl border border-n-weak bg-n-solid-1 p-5 mb-6">
        <h3
          class="font-headline text-[14.5px] font-semibold text-n-slate-12 mb-1"
        >
          {{ t('SITES.NEW.TITLE') }}
        </h3>
        <p class="text-[13px] text-n-slate-10 mb-4">
          {{ t('SITES.NEW.DESCRIPTION') }}
        </p>
        <div class="flex items-end gap-3 flex-wrap">
          <div class="w-56">
            <Input
              v-model="newName"
              :label="t('SITES.NEW.NAME_LABEL')"
              :placeholder="t('SITES.NEW.NAME_PLACEHOLDER')"
            />
          </div>
          <div class="w-64">
            <Input
              v-model="newDomain"
              :label="t('SITES.NEW.DOMAIN_LABEL')"
              :placeholder="t('SITES.NEW.DOMAIN_PLACEHOLDER')"
            />
          </div>
          <Button
            :label="t('SITES.NEW.CREATE')"
            icon="i-lucide-plus"
            :is-loading="isSaving"
            :disabled="!newName.trim() || !newDomain.trim()"
            @click="createProperty"
          />
        </div>
      </div>

      <SettingsListToolbar
        v-model="searchQuery"
        :placeholder="t('SITES.SEARCH')"
        :count-label="t('SITES.COUNT', properties.length)"
      />
      <SettingsTable
        :headers="headers"
        :is-empty="!filtered.length"
        :empty-message="t('SITES.EMPTY')"
      >
        <tr
          v-for="property in filtered"
          :key="property.id"
          class="group hover:bg-n-solid-2 transition-colors"
        >
          <!-- Site -->
          <td class="px-5 py-3 align-top">
            <span class="font-medium text-n-slate-12 text-sm block">
              {{ property.name }}
            </span>
            <span class="block text-[11px] font-mono text-n-slate-9 mt-0.5">
              {{ property.domain }}
            </span>
          </td>

          <!-- Tokens -->
          <td class="px-5 py-3 align-top">
            <div class="flex flex-col gap-1.5">
              <div class="flex items-center gap-1.5">
                <span
                  class="text-[10px] uppercase tracking-wide text-n-slate-9 w-16 shrink-0"
                >
                  {{ t('SITES.TOKEN_PUBLIC') }}
                </span>
                <code
                  class="text-[11px] text-n-slate-11 truncate max-w-[12rem]"
                >
                  {{ property.public_token }}
                </code>
                <button
                  class="i-lucide-copy size-3.5 text-n-slate-9 hover:text-n-slate-12"
                  :title="t('SITES.COPY')"
                  @click="copy(property.public_token)"
                />
              </div>
              <div class="flex items-center gap-1.5">
                <span
                  class="text-[10px] uppercase tracking-wide text-n-slate-9 w-16 shrink-0"
                >
                  {{ t('SITES.TOKEN_SECRET') }}
                </span>
                <code
                  class="text-[11px] text-n-slate-11 truncate max-w-[12rem]"
                >
                  {{
                    revealed[property.id]
                      ? property.secret_token
                      : masked(property.secret_token)
                  }}
                </code>
                <button
                  class="size-3.5 text-n-slate-9 hover:text-n-slate-12"
                  :class="
                    revealed[property.id] ? 'i-lucide-eye-off' : 'i-lucide-eye'
                  "
                  :title="t('SITES.REVEAL')"
                  @click="toggleReveal(property)"
                />
                <button
                  class="i-lucide-copy size-3.5 text-n-slate-9 hover:text-n-slate-12"
                  :title="t('SITES.COPY')"
                  @click="copy(property.secret_token)"
                />
                <button
                  class="i-lucide-refresh-cw size-3.5 text-n-slate-9 hover:text-n-ruby-9"
                  :title="t('SITES.REGENERATE')"
                  @click="regenerate(property)"
                />
              </div>
            </div>
          </td>

          <!-- Meta: quem manda + pixel + evento -->
          <td class="px-5 py-3 align-top">
            <div class="flex flex-col gap-2 max-w-[18rem]">
              <select
                :value="property.capi_sender"
                class="!mb-0 !h-8 rounded-lg text-[12px]"
                @change="
                  saveField(property, 'capi_sender', $event.target.value)
                "
              >
                <option v-for="s in SENDERS" :key="s" :value="s">
                  {{ t(`SITES.SENDER.${s}`) }}
                </option>
              </select>
              <template v-if="property.capi_sender === 'crm'">
                <select
                  :value="property.meta_capi_pixel_id || ''"
                  class="!mb-0 !h-8 rounded-lg text-[12px]"
                  @change="
                    saveField(
                      property,
                      'meta_capi_pixel_id',
                      $event.target.value || null
                    )
                  "
                >
                  <option value="">{{ t('SITES.PIXEL_NONE') }}</option>
                  <option
                    v-for="pixel in pixels"
                    :key="pixel.id"
                    :value="pixel.id"
                  >
                    {{ pixel.name }}
                  </option>
                </select>
                <input
                  :value="property.meta_capi_event"
                  :placeholder="t('SITES.EVENT_PLACEHOLDER')"
                  class="!mb-0 !h-8 rounded-lg text-[12px]"
                  @change="
                    saveField(property, 'meta_capi_event', $event.target.value)
                  "
                />
                <span
                  v-if="!property.crm_sends_capi"
                  class="text-[11px] text-n-amber-11"
                >
                  {{ t('SITES.NEEDS_PIXEL') }}
                </span>
              </template>
              <span v-else class="text-[11px] text-n-slate-10">
                {{ t(`SITES.SENDER_HINT.${property.capi_sender}`) }}
              </span>
            </div>
          </td>

          <!-- Acoes -->
          <td class="px-5 py-3 align-top text-right">
            <Button
              icon="i-lucide-trash-2"
              variant="ghost"
              color="ruby"
              size="xs"
              :title="t('SITES.DELETE')"
              @click="remove(property)"
            />
          </td>
        </tr>
      </SettingsTable>

      <!-- Como ligar o plugin -->
      <div
        class="rounded-xl border border-n-weak bg-n-solid-1 p-5 mt-6 text-[13px] text-n-slate-11"
      >
        <h3
          class="font-headline text-[14px] font-semibold text-n-slate-12 mb-2"
        >
          {{ t('SITES.HOWTO.TITLE') }}
        </h3>
        <p class="mb-1">{{ t('SITES.HOWTO.PAGEVIEW') }}</p>
        <p class="mb-1">
          {{ t('SITES.HOWTO.CONVERSION', { header: HEADER_NAME }) }}
        </p>
        <p class="mb-0 text-n-slate-10">{{ t('SITES.HOWTO.SENDER') }}</p>
      </div>
    </template>
  </SettingsPageLayout>
</template>
